import React from "react";
import Table from "../../../../common/components/molecule/Table/Table";
import ButtonV2 from "../../../../common/components/atom/ButtonV2/Button";
import TagChip from "../../../../common/components/molecule/TagChip/TagChip";
import { ExtractJobDTO } from "../../types/extract.types";
import {
  useExtractJobsList,
  useDownloadExtractJob,
} from "../../hooks/useExtractJobs";
import {
  aggregationLabel,
  formatBytes,
  formatDateRange,
  formatDateTime,
  statusBadge,
} from "../../utils/format";

const StatusPill: React.FC<{ status: ExtractJobDTO["status"] }> = ({ status }) => {
  const { label, color, bg } = statusBadge(status);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 12,
        background: bg,
        color,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
};

const ProgressCell: React.FC<{ job: ExtractJobDTO }> = ({ job }) => {
  if (job.status !== "running" && job.status !== "pending") {
    return <span>—</span>;
  }
  const pct = Math.max(0, Math.min(100, job.progress || 0));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width: 80,
          height: 6,
          background: "#e5e7eb",
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: "#0c5273",
            transition: "width 200ms linear",
          }}
        />
      </div>
      <span style={{ fontSize: 12, color: "#374151" }}>{pct}%</span>
    </div>
  );
};

const TagsCell: React.FC<{ tags: string[] }> = ({ tags }) => {
  if (!tags || tags.length === 0) return <span style={{ color: "#9ca3af" }}>Tous</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {tags.map((t) => (
        <TagChip key={t} label={t} />
      ))}
    </div>
  );
};

export default function ExtractJobsListView() {
  const { data, isLoading, refetch } = useExtractJobsList();
  const download = useDownloadExtractJob();

  const headers = [
    "Créé le",
    "Type",
    "Période",
    "Mode",
    "Tags",
    "Statut",
    "Progression",
    "Lignes",
    "Taille",
    "Actions",
  ];

  const formatType = (job: ExtractJobDTO): string => {
    const group = job.groupName?.trim();
    const extract = job.extractName?.trim();
    if (group && extract) return `${group} – ${extract}`;
    return group || extract || "—";
  };

  const rows = (data?.jobs ?? []).map((job) => ({
    id: job.id,
    data: [
      formatDateTime(job.createdAt),
      formatType(job),
      formatDateRange(job.startDate, job.endDate),
      aggregationLabel(job.aggregation),
      <TagsCell key="tags" tags={job.tags || []} />,
      <StatusPill key="status" status={job.status} />,
      <ProgressCell key="prog" job={job} />,
      job.rowCount ? job.rowCount.toLocaleString("fr-FR") : "—",
      formatBytes(job.fileSize),
      <ButtonV2
        key="dl"
        variant="outlined"
        size="small"
        disabled={
          job.status !== "done" || download.isLoading
        }
        onClick={() => download.mutate(job)}
      >
        {job.status === "expired" ? "Expiré" : "Télécharger"}
      </ButtonV2>,
    ],
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, color: "#111827" }}>
          Mes extractions
        </h2>
        <ButtonV2 variant="bordered" size="small" onClick={() => refetch()}>
          Actualiser
        </ButtonV2>
      </div>

      <Table
        headerTitle={headers}
        bodyData={rows}
        isLoading={isLoading}
        emptyMessage="Aucune extraction pour ce compte."
      />

      {download.isError && (
        <div style={{ color: "#b91c1c", fontSize: 13 }}>
          {(download.error as Error).message}
        </div>
      )}
    </div>
  );
}
